import { Button } from "@mixitone/components";
import { ApplicationView } from "@mixitone/mvc";
import React from "react";
import { useNavigate } from "react-router-dom";
import { DashLayout } from "../../Components/DashLayout";
import { MessageBanner } from "../../Components/MessageBanner";
import { AuthController } from "../../controllers/AuthController";
import { injectParams } from "../../lib/injectParams";
import AccordionSection from "./components/AccordionSection";
import EmailValidationForm from "./components/EmailValidationForm";
import EmailValidationMessage from "./components/EmailValidationMessage";
import FinalInformationSection from "./components/FinalInformationSection";
import MobileNumberForm from "./components/MobileNumberForm";
import NotFoundState from "./components/NotFoundState";
import PaymentSelection from "./components/PaymentSelection";
import RegistrationClosedState from "./components/RegistrationClosedState";
import TournamentDetailsSection from "./components/TournamentDetailsSection";
import { CompetitionRegistrationPageController } from "./controllers/CompetitionRegistrationPageController";

const ControlledCompetitionRegistrationPage: React.FC = () => {
  const controller = CompetitionRegistrationPageController.use();
  const authController = AuthController.use();
  const navigate = useNavigate();
  const {
    tournament,
    competition,
    message,
    messageType,
    validationEmailSent,
    activeSection,
    completedSections,
    saving,
    akahuPolling,
    akahuAwaitingConfirmation,
  } = controller.state;
  const { loading, isDoubles, isPartner, akahuPaymentLocked } = controller;
  const emailIsVerified = authController.emailIsVerified;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await controller.actionSubmit();
  };

  const handleBack = () => {
    // Clear validation email sent state when navigating away
    if (controller.state.validationEmailSent) {
      controller.clearValidationEmailSent();
    }
    if (tournament) {
      navigate(`/tournament/${tournament.portal_slug}`);
    } else {
      navigate("/");
    }
  };

  const handleViewEntry = () => {
    if (tournament?.portal_slug && competition?.id) {
      navigate(`/in/tournaments/${tournament.portal_slug}/manage/${competition.id}`);
    } else {
      navigate("/in/tournaments");
    }
  };

  if (loading) {
    return <RegistrationLoadingSkeleton />;
  }

  if (!tournament || !competition) {
    return <NotFoundState />;
  }

  if (!competition.isEntryOpen) {
    return <RegistrationClosedState onBack={handleBack} />;
  }

  const title = competition.name
    ? controller.state.partnerCode
      ? `Join ${competition.name}`
      : `Register for ${competition.name}`
    : "Tournament Registration";

  // If validation email was sent, show the validation message
  if (validationEmailSent && !emailIsVerified) {
    return (
      <DashLayout title={title}>
        <div className="pb-6">
          {message && (
            <MessageBanner
              message={message}
              type={messageType || "error"}
              onClose={() => controller.actionClearMessage()}
            />
          )}
          <EmailValidationMessage competitionName={competition.name!} />
          <div className="mt-6">
            <Button type="button" kind="secondary" onClick={handleBack}>
              Back
            </Button>
          </div>
        </div>
      </DashLayout>
    );
  }

  return (
    <DashLayout title={title}>
      <form onSubmit={handleSubmit} className="pb-6">
        <div>
          {message && (
            <MessageBanner
              message={message}
              type={messageType || "error"}
              onClose={() => controller.actionClearMessage()}
            />
          )}

          {/* Tournament Details Section */}
          <AccordionSection
            title="Tournament Details"
            isActive={activeSection === 0}
            isCompleted={completedSections.includes(0)}
            canOpen={controller.canMoveToSection(0)}
            onOpen={() => controller.actionSetActiveSection(0)}
            showNextButton={true}
            onNext={() => {
              controller.actionMoveToNextSection();
            }}
          >
            <TournamentDetailsSection />
          </AccordionSection>

          {/* Name and Email Section */}
          <AccordionSection
            title="Name and email"
            isActive={activeSection === 1}
            isCompleted={completedSections.includes(1)}
            canOpen={controller.canMoveToSection(1)}
            onOpen={() => controller.actionSetActiveSection(1)}
            showNextButton={true}
            nextButtonText={emailIsVerified ? "Next" : "Send validation email"}
            nextButtonDisabled={saving}
            onNext={async () => {
              if (emailIsVerified) {
                controller.actionCompleteSection(1);
                controller.actionMoveToNextSection();
              } else {
                await controller.actionSubmit();
              }
            }}
          >
            <EmailValidationForm />
          </AccordionSection>

          {/* Mobile Number Section */}
          <AccordionSection
            title="Contact information"
            isActive={activeSection === 2}
            isCompleted={completedSections.includes(2)}
            canOpen={controller.canMoveToSection(2)}
            onOpen={() => controller.actionSetActiveSection(2)}
            showNextButton={true}
            nextButtonDisabled={saving}
            onNext={async () => {
              await controller.actionSubmit();
              // After submitting mobile number, the registration is created
              // and we move to the payment section
            }}
          >
            <MobileNumberForm />
          </AccordionSection>

          {/* Payment Section - Only show if payment is required */}
          {tournament.requiresPayment && (
            <AccordionSection
              title="Payment"
              isActive={activeSection === 3}
              isCompleted={completedSections.includes(3)}
              canOpen={controller.canMoveToSection(3)}
              onOpen={() => controller.actionSetActiveSection(3)}
              showNextButton={true}
              nextButtonText={
                akahuPolling
                  ? "Confirming payment..."
                  : akahuAwaitingConfirmation
                    ? "Waiting for payment..."
                    : "Next"
              }
              nextButtonDisabled={saving || akahuPaymentLocked}
              onNext={async () => {
                await controller.actionSubmit();
                // After submitting payment, the payment is processed
                // and we move to the final section
              }}
            >
              <PaymentSelection />
            </AccordionSection>
          )}

          {/* Final Information Section */}
          <AccordionSection
            title={isDoubles && !isPartner ? "Register your doubles partner" : "Registration complete"}
            isActive={activeSection === 4}
            isCompleted={completedSections.includes(4)}
            canOpen={controller.canMoveToSection(4)}
            onOpen={() => controller.actionSetActiveSection(4)}
          >
            <FinalInformationSection />
          </AccordionSection>
        </div>

        <div className={`mt-6 flex ${activeSection === 4 ? "justify-end" : ""}`}>
          {activeSection === 4 ? (
            <Button type="button" kind="primary" onClick={handleViewEntry}>
              View your entry
            </Button>
          ) : (
            <Button type="button" kind="secondary" onClick={handleBack}>
              Back to Tournament
            </Button>
          )}
        </div>
      </form>
    </DashLayout>
  );
};

// Need to import this here to avoid circular dependency
import RegistrationLoadingSkeleton from "./components/RegistrationLoadingSkeleton";

const CompetitionRegistrationPage = injectParams(
  CompetitionRegistrationPageController.scope(ApplicationView(ControlledCompetitionRegistrationPage)),
);

export { CompetitionRegistrationPage };
